import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Configuration des tours d'échafauds par zone du BT.
 * Approche simplifiée :
 *   - 1 tour = 1 ligne (type template + nb étages + distance au mur)
 *   - Cases à cocher pour fixations murales + équerres par étage
 *   - Preview live du matériel (appel backend /api/echafaudages/calculer-materiel-tour)
 *
 * Props:
 *   zones: [{numero, nom, photo_url}]           — zones du BT
 *   typesEtages: [{id, type, dimensions, ...}]  — templates disponibles (depuis remorque)
 *   remorqueId: str                             — id de la remorque sélectionnée
 *   value: [] / onChange: fn
 */
export function ToursEchafaudsConfig({
  zones = [],
  typesEtages = [],
  remorqueId = null,
  value = [],
  onChange = () => {},
}) {
  const [config, setConfig] = useState(value || []);

  useEffect(() => {
    setConfig(value || []);
  }, [value]);

  const updateConfig = useCallback((next) => {
    setConfig(next);
    onChange(next);
  }, [onChange]);

  const ajouterTour = () => {
    const defaultZone = zones[0]?.numero || null;
    const defaultTemplate = typesEtages[0]?.id || '';
    const nouvelle = {
      id: `tour_${Date.now()}`,
      zone_bt_id: defaultZone,
      template_id: defaultTemplate,
      nb_etages: 3,
      distance_mur_inch: 0,
      terrain_pente: false,
      etages_avec_fixation: [],
      etages_avec_equerre: [],
    };
    updateConfig([...config, nouvelle]);
  };

  const supprimerTour = (idx) => {
    if (!window.confirm('Supprimer cette tour ?')) return;
    updateConfig(config.filter((_, i) => i !== idx));
  };

  const patchTour = (idx, patch) => {
    const next = [...config];
    next[idx] = { ...next[idx], ...patch };
    updateConfig(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">🏗 Tours d'échafauds à monter</p>
        <Button
          type="button"
          size="sm"
          onClick={ajouterTour}
          disabled={!remorqueId || zones.length === 0 || typesEtages.length === 0}
          className="h-8 text-xs bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="btn-ajouter-tour"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une tour
        </Button>
      </div>

      {!remorqueId && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
          Sélectionnez d'abord une remorque d'échafauds pour configurer vos tours.
        </p>
      )}
      {remorqueId && typesEtages.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
          Aucun type d'étage configuré sur cette remorque. Configurez-les dans Unités → [votre remorque].
        </p>
      )}
      {remorqueId && zones.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
          Ajoutez au moins une zone au BT avant de configurer les tours.
        </p>
      )}

      {config.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-2 text-center">
          Aucune tour configurée. Cliquez sur "Ajouter une tour" pour commencer.
        </p>
      ) : (
        <div className="space-y-3">
          {config.map((tour, idx) => (
            <TourRow
              key={tour.id || idx}
              tour={tour}
              zones={zones}
              typesEtages={typesEtages}
              remorqueId={remorqueId}
              onChange={(patch) => patchTour(idx, patch)}
              onDelete={() => supprimerTour(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ligne d'une tour ───────────────────────────────────────────────
function TourRow({ tour, zones, typesEtages, remorqueId, onChange, onDelete }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const nbEtages = tour.nb_etages || 1;

  // Recalcul du preview dès qu'un paramètre change
  useEffect(() => {
    if (!remorqueId || !tour.template_id || !tour.nb_etages) {
      setPreview(null);
      return;
    }
    let cancel = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          `${API_URL}/api/echafaudages/calculer-materiel-tour`,
          {
            remorque_id: remorqueId,
            template_id: tour.template_id,
            nb_etages: tour.nb_etages,
            distance_mur_inch: tour.distance_mur_inch || 0,
            terrain_pente: !!tour.terrain_pente,
            etages_avec_fixation: tour.etages_avec_fixation || [],
            etages_avec_equerre: tour.etages_avec_equerre || [],
          }
        );
        if (!cancel) setPreview(res.data);
      } catch (e) {
        if (!cancel) setPreview({ error: e?.response?.data?.detail || e.message });
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
  }, [
    remorqueId,
    tour.template_id,
    tour.nb_etages,
    tour.distance_mur_inch,
    tour.terrain_pente,
    tour.etages_avec_fixation,
    tour.etages_avec_equerre,
  ]);

  const toggleFixation = (niveau) => {
    const curr = tour.etages_avec_fixation || [];
    const next = curr.includes(niveau) ? curr.filter((n) => n !== niveau) : [...curr, niveau].sort((a, b) => a - b);
    onChange({ etages_avec_fixation: next });
  };
  const toggleEquerre = (niveau) => {
    const curr = tour.etages_avec_equerre || [];
    const next = curr.includes(niveau) ? curr.filter((n) => n !== niveau) : [...curr, niveau].sort((a, b) => a - b);
    onChange({ etages_avec_equerre: next });
  };

  const zoneNom = zones.find((z) => z.numero === tour.zone_bt_id)?.nom || `Zone ${tour.zone_bt_id}`;

  return (
    <div className="border-2 border-orange-200 bg-white rounded-lg p-3 space-y-3" data-testid={`tour-${tour.id}`}>
      {/* ─── Header : zone + supprimer ─── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-orange-900">
          📍 {zoneNom}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onDelete} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* ─── Ligne de saisie principale ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[11px] text-slate-600 font-medium">Zone</label>
          <Select
            value={String(tour.zone_bt_id || '')}
            onValueChange={(v) => onChange({ zone_bt_id: parseInt(v, 10) })}
          >
            <SelectTrigger className="h-9 text-xs" data-testid={`tour-zone-${tour.id}`}>
              <SelectValue placeholder="Choisir zone" />
            </SelectTrigger>
            <SelectContent>
              {zones.map((z) => (
                <SelectItem key={z.numero} value={String(z.numero)}>
                  Zone {z.numero} — {z.nom || 'Sans nom'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] text-slate-600 font-medium">Type</label>
          <Select value={tour.template_id || ''} onValueChange={(v) => onChange({ template_id: v })}>
            <SelectTrigger className="h-9 text-xs" data-testid={`tour-template-${tour.id}`}>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {typesEtages.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.type} {t.dimensions ? `(${t.dimensions})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] text-slate-600 font-medium">Nb étages</label>
          <Input
            type="number"
            min="1"
            max="10"
            value={tour.nb_etages || ''}
            onChange={(e) => onChange({ nb_etages: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            className="h-9 text-xs"
            data-testid={`tour-nb-etages-${tour.id}`}
          />
        </div>
        <div>
          <label className="text-[11px] text-slate-600 font-medium">Distance mur (″)</label>
          <Input
            type="number"
            min="0"
            max="120"
            value={tour.distance_mur_inch === 0 ? '' : (tour.distance_mur_inch || '')}
            onChange={(e) => onChange({ distance_mur_inch: parseInt(e.target.value, 10) || 0 })}
            placeholder="40"
            className="h-9 text-xs"
            data-testid={`tour-distance-${tour.id}`}
          />
        </div>
      </div>

      {/* ─── Terrain en pente ─── */}
      <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
        <input
          type="checkbox"
          checked={!!tour.terrain_pente}
          onChange={(e) => onChange({ terrain_pente: e.target.checked })}
          className="w-3.5 h-3.5"
          data-testid={`tour-pente-${tour.id}`}
        />
        Terrain en pente — utiliser cadres 5×3 pour la base
      </label>

      {/* ─── Sélection étage par étage fixations/équerres ─── */}
      <div className="bg-slate-50 border border-slate-200 rounded p-2 space-y-2">
        <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
          Emplacement fixations & équerres
        </p>
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <span className="font-medium text-slate-600">Étage</span>
          <div className="flex gap-1 flex-wrap">
            {Array.from({ length: nbEtages }, (_, i) => i + 1).map((n) => (
              <span key={n} className="w-7 h-7 flex items-center justify-center bg-white border border-slate-300 rounded font-semibold text-slate-700">
                {n}
              </span>
            ))}
          </div>

          <span className="font-medium text-slate-600 self-center">📍 Fixation</span>
          <div className="flex gap-1 flex-wrap">
            {Array.from({ length: nbEtages }, (_, i) => i + 1).map((n) => {
              const active = (tour.etages_avec_fixation || []).includes(n);
              return (
                <button
                  type="button"
                  key={n}
                  onClick={() => toggleFixation(n)}
                  className={`w-7 h-7 rounded border text-[11px] font-bold transition ${
                    active ? 'bg-red-500 text-white border-red-600' : 'bg-white text-slate-400 border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid={`tour-fixation-${tour.id}-${n}`}
                >
                  {active ? '✓' : ''}
                </button>
              );
            })}
          </div>

          <span className="font-medium text-slate-600 self-center">📐 Équerre</span>
          <div className="flex gap-1 flex-wrap">
            {Array.from({ length: nbEtages }, (_, i) => i + 1).map((n) => {
              const active = (tour.etages_avec_equerre || []).includes(n);
              return (
                <button
                  type="button"
                  key={n}
                  onClick={() => toggleEquerre(n)}
                  className={`w-7 h-7 rounded border text-[11px] font-bold transition ${
                    active ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-slate-400 border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid={`tour-equerre-${tour.id}-${n}`}
                >
                  {active ? '✓' : ''}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Preview matériel ─── */}
      <PreviewMateriel preview={preview} loading={loading} />
    </div>
  );
}

function PreviewMateriel({ preview, loading }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calcul du matériel...
      </div>
    );
  }
  if (!preview) return null;
  if (preview.error) {
    return (
      <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
        ⚠️ {preview.error}
      </div>
    );
  }
  const pieces = preview.pieces_necessaires || [];
  const avertissements = preview.avertissements || [];
  const ok = preview.regles_respectees;

  return (
    <div className="space-y-2">
      {avertissements.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded p-2 space-y-1" data-testid="preview-avertissements">
          {avertissements.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-red-800 font-medium">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}
      {avertissements.length === 0 && ok && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded">
          <CheckCircle2 className="w-3.5 h-3.5" /> Configuration conforme aux règles de sécurité
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded p-2" data-testid="preview-materiel">
        <p className="text-[11px] font-semibold text-blue-900 uppercase tracking-wide mb-1">📊 Matériel calculé</p>
        {pieces.length === 0 ? (
          <p className="text-[11px] text-slate-500 italic">Aucun matériel</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
            {pieces.map((p, i) => (
              <div
                key={i}
                className={`text-[11px] flex items-center justify-between gap-2 ${
                  p.disponible ? 'text-slate-700' : 'text-red-700 font-semibold'
                }`}
              >
                <span className="truncate">
                  <strong>{p.qte_necessaire}×</strong> {p.nom}
                </span>
                <span className="text-[10px] shrink-0 tabular-nums">
                  {p.disponible ? '✅' : `⚠️ ${p.qte_disponible} dispo`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
